/**
 * The chefs.
 *
 * Two avatars on one screen have to answer three questions instantly: which one
 * am I, which way am I facing, and what am I holding. Round 1 of the visual
 * pass answered all three technically and none of them at a glance — a chef was
 * a coloured disc with a white hat, and facing was a soft gradient cone the
 * reviewer had to hunt for. "Use what you face" is the whole interaction model,
 * so an ambiguous facing is a lost round.
 *
 * Round 2 makes facing a **statement about a tile** rather than a hint about a
 * body. Three cues, in descending size:
 *
 *   1. corner brackets around the cell the chef would act on, in seat colour
 *      with a dark keyline, drawn on top of the station,
 *   2. a solid chevron on the boundary between the chef and that cell,
 *   3. the body itself — head, arms, boots and eyes all turn.
 *
 * Kill any two of those and it still reads, which is the bar a load-bearing cue
 * has to clear.
 *
 * Seats stay distinguished on three independent channels, never on colour
 * alone: a colour, a **build** (square shoulders against a round barrel), and a
 * hat. Two players who both have a red/green deficiency can still say "the tall
 * toque" and "the flat cap" — and the roster in the HUD draws the same two
 * silhouettes next to their names, so the vocabulary is taught rather than
 * assumed.
 */

import type { Cell, Direction, Player } from '@overcooked/shared'
import { DIRECTION_VECTORS } from '@overcooked/shared'
import type { Catalog } from './catalog'
import { drawCarryable } from './icons'
import type { SeatIdentity } from './theme'
import { COLOURS, roundRect, seatIdentity, shade } from './theme'

const TAU = Math.PI * 2

/** Which cell a press would act on. Pure, and the same answer the engine gives. */
export function facingCell(cell: Cell, facing: Direction): Cell {
  const v = DIRECTION_VECTORS[facing]
  return { x: cell.x + v.x, y: cell.y + v.y }
}

/** The two axes of a facing: forward, and the chef's own left-right. */
function frame(facing: Direction): { fx: number; fy: number; px: number; py: number } {
  const v = DIRECTION_VECTORS[facing]
  return { fx: v.x, fy: v.y, px: -v.y, py: v.x }
}

interface PlayerBox {
  /** Tile centre. */
  cx: number
  cy: number
  /** Tile size, so the avatar scales with the map. */
  tile: number
}

/**
 * The brackets around the tile a chef is aimed at.
 *
 * Drawn by `loop.ts` after the stations and before the bodies, so it sits on
 * top of the fixture it is pointing at and underneath anybody standing there.
 * Four corners rather than a full outline: a closed rectangle competes with the
 * burn warning ring, and corners read as "targeted" rather than as "on fire".
 */
export function drawFocus(
  ctx: CanvasRenderingContext2D,
  seat: SeatIdentity,
  x: number,
  y: number,
  size: number,
  facing: Direction,
): void {
  const arm = size * 0.26
  const lw = Math.max(2, size * 0.055)
  const inset = lw * 0.75

  ctx.save()
  ctx.lineCap = 'square'
  ctx.lineJoin = 'miter'
  for (const pass of ['keyline', 'colour'] as const) {
    ctx.strokeStyle = pass === 'keyline' ? 'rgba(10, 8, 6, 0.75)' : seat.colour
    ctx.lineWidth = pass === 'keyline' ? lw * 1.9 : lw
    for (const [sx, sy] of [
      [0, 0],
      [1, 0],
      [0, 1],
      [1, 1],
    ]) {
      const px = x + inset + sx * (size - inset * 2)
      const py = y + inset + sy * (size - inset * 2)
      const dx = sx === 0 ? 1 : -1
      const dy = sy === 0 ? 1 : -1
      ctx.beginPath()
      ctx.moveTo(px + dx * arm, py)
      ctx.lineTo(px, py)
      ctx.lineTo(px, py + dy * arm)
      ctx.stroke()
    }
  }

  // The chevron, on the edge the chef is standing against. It is what turns
  // four brackets into a direction: the target is marked and the arrow says
  // whose it is and where it came from.
  const { fx, fy } = frame(facing)
  const cx = x + size / 2 - fx * size * 0.5
  const cy = y + size / 2 - fy * size * 0.5
  const w = size * 0.2
  const h = size * 0.17
  ctx.beginPath()
  ctx.moveTo(cx + fx * h, cy + fy * h)
  ctx.lineTo(cx - fx * h * 0.5 - fy * w, cy - fy * h * 0.5 + fx * w)
  ctx.lineTo(cx - fx * h * 0.5 + fy * w, cy - fy * h * 0.5 - fx * w)
  ctx.closePath()
  ctx.fillStyle = seat.colour
  ctx.fill()
  ctx.lineWidth = Math.max(1.5, size * 0.032)
  ctx.strokeStyle = 'rgba(10, 8, 6, 0.8)'
  ctx.stroke()
  ctx.restore()
}

export function drawPlayer(
  ctx: CanvasRenderingContext2D,
  player: Player,
  box: PlayerBox,
  isSelf: boolean,
  catalog: Catalog,
  atTick: number,
): void {
  const seat = seatIdentity(player.seat)
  const { cx, cy, tile } = box
  const r = tile * 0.36

  ctx.save()
  ctx.globalAlpha = player.connected ? 1 : 0.4

  // Ground shadow, so the chef sits on the floor rather than floating on it.
  ctx.beginPath()
  ctx.ellipse(cx, cy + r * 0.94, r * 0.82, r * 0.28, 0, 0, TAU)
  ctx.fillStyle = 'rgba(20, 12, 6, 0.3)'
  ctx.fill()

  // Self marker: a broken ring on the floor, under the body. A shape, so
  // "which one am I" never depends on remembering a colour.
  if (isSelf) {
    ctx.save()
    ctx.beginPath()
    ctx.setLineDash([r * 0.5, r * 0.36])
    ctx.lineWidth = Math.max(2, r * 0.12)
    ctx.strokeStyle = COLOURS.paper
    ctx.ellipse(cx, cy + r * 0.9, r * 0.98, r * 0.4, 0, 0, TAU)
    ctx.stroke()
    ctx.restore()
  }

  drawChef(ctx, seat, cx, cy, r, player.facing)

  // What you are carrying rides in front of you, in the direction you face, so
  // it is obvious which station it is about to go into.
  if (player.holding) {
    const { fx, fy } = frame(player.facing)
    drawCarryable(
      ctx,
      player.holding,
      // Out in front and low, at the height a pair of hands would be. Round 1
      // put it at head height, where an extinguisher covered the chef's face.
      cx + fx * r * 1.02,
      cy + fy * r * 0.76 + r * 0.06,
      r * 0.66,
      catalog,
      atTick,
    )
  }

  ctx.globalAlpha = 1
  ctx.restore()
}

/**
 * The body, from the feet up.
 *
 * `r` is roughly a third of a tile, and everything is measured from it, so the
 * same chef reads at the 33 px tile a 10x15 map gets and at the 110 px tile a
 * 5x5 map gets.
 */
function drawChef(
  ctx: CanvasRenderingContext2D,
  seat: SeatIdentity,
  cx: number,
  cy: number,
  r: number,
  facing: Direction,
): void {
  const { fx, fy, px, py } = frame(facing)
  const lw = Math.max(1.4, r * 0.11)
  const line = shade(seat.shade, -0.25)
  const broad = seat.build === 'broad'

  // Boots. Small, dark, and offset the way the chef is walking — the lowest
  // and least obtrusive of the facing cues, and the one that survives the
  // body being covered by whatever is being carried.
  for (const side of [-1, 1]) {
    ctx.beginPath()
    ctx.ellipse(
      cx + px * side * r * 0.34 + fx * r * 0.24,
      cy + py * side * r * 0.2 + fy * r * 0.18 + r * 0.82,
      r * 0.26,
      r * 0.17,
      0,
      0,
      TAU,
    )
    ctx.fillStyle = '#2b211a'
    ctx.fill()
  }

  // Arms, behind the torso, one each side. They give the silhouette a width
  // that a plain disc does not have and they turn with the body.
  for (const side of [-1, 1]) {
    ctx.beginPath()
    ctx.ellipse(
      cx + px * side * r * 0.82 + fx * r * 0.1,
      cy + py * side * r * 0.5 + fy * r * 0.08 + r * 0.24,
      r * 0.24,
      r * 0.34,
      Math.atan2(py * side, px * side),
      0,
      TAU,
    )
    ctx.fillStyle = seat.shade
    ctx.fill()
    ctx.lineWidth = lw * 0.8
    ctx.strokeStyle = line
    ctx.stroke()
  }

  // The torso. Square shoulders or a round barrel — the channel that tells the
  // seats apart in greyscale.
  ctx.beginPath()
  if (broad) {
    ctx.moveTo(cx - r * 0.86, cy + r * 0.82)
    ctx.lineTo(cx - r * 0.86, cy - r * 0.06)
    ctx.quadraticCurveTo(cx - r * 0.86, cy - r * 0.4, cx - r * 0.54, cy - r * 0.4)
    ctx.lineTo(cx + r * 0.54, cy - r * 0.4)
    ctx.quadraticCurveTo(cx + r * 0.86, cy - r * 0.4, cx + r * 0.86, cy - r * 0.06)
    ctx.lineTo(cx + r * 0.86, cy + r * 0.82)
    ctx.quadraticCurveTo(cx, cy + r * 1.0, cx - r * 0.86, cy + r * 0.82)
    ctx.closePath()
  } else {
    ctx.ellipse(cx, cy + r * 0.3, r * 0.8, r * 0.72, 0, 0, TAU)
  }
  ctx.fillStyle = seat.colour
  ctx.fill()
  ctx.lineWidth = lw
  ctx.strokeStyle = line
  ctx.stroke()

  // The apron, clipped to the torso: white, and a different cut per seat.
  ctx.save()
  ctx.clip()
  ctx.fillStyle = '#f6f1e6'
  const w = r * 0.62
  switch (seat.apron) {
    case 'square':
      ctx.fillRect(cx - w / 2, cy - r * 0.06, w, r * 1.2)
      break
    case 'round':
      ctx.beginPath()
      ctx.arc(cx, cy + r * 0.46, r * 0.4, 0, TAU)
      ctx.fill()
      break
    case 'notched':
      ctx.beginPath()
      ctx.moveTo(cx - w / 2, cy - r * 0.1)
      ctx.lineTo(cx + w / 2, cy - r * 0.1)
      ctx.lineTo(cx + w / 2, cy + r * 0.95)
      ctx.lineTo(cx, cy + r * 0.55)
      ctx.lineTo(cx - w / 2, cy + r * 0.95)
      ctx.closePath()
      ctx.fill()
      break
    default:
      ctx.beginPath()
      ctx.moveTo(cx - w / 2, cy - r * 0.1)
      ctx.lineTo(cx + w / 2, cy - r * 0.1)
      ctx.lineTo(cx, cy + r * 1.0)
      ctx.closePath()
      ctx.fill()
      break
  }
  ctx.restore()

  // The neckerchief: a wedge of seat colour under the chin. It is the one bit
  // of the seat's colour that stays visible when the chef is walking away from
  // the camera and the hat and the apron are all that is left.
  const hx = cx + fx * r * 0.2
  const hy = cy + fy * r * 0.12 - r * 0.5
  ctx.beginPath()
  ctx.moveTo(hx - r * 0.42, hy + r * 0.3)
  ctx.lineTo(hx + r * 0.42, hy + r * 0.3)
  ctx.lineTo(hx, hy + r * 0.74)
  ctx.closePath()
  ctx.fillStyle = shade(seat.colour, 0.18)
  ctx.fill()
  ctx.lineWidth = lw * 0.7
  ctx.strokeStyle = line
  ctx.stroke()

  drawHead(ctx, seat, hx, hy, r, facing, lw)
}

function drawHead(
  ctx: CanvasRenderingContext2D,
  seat: SeatIdentity,
  cx: number,
  cy: number,
  r: number,
  facing: Direction,
  lw: number,
): void {
  ctx.beginPath()
  ctx.arc(cx, cy, r * 0.48, 0, TAU)
  ctx.fillStyle = '#eec69c'
  ctx.fill()
  ctx.lineWidth = lw
  ctx.strokeStyle = '#a3764d'
  ctx.stroke()

  // A face, and only when there is one to see. A chef walking away shows the
  // back of their head — a third facing cue, and free.
  if (facing === 'up') {
    ctx.beginPath()
    ctx.arc(cx, cy, r * 0.4, 0, TAU)
    ctx.fillStyle = '#8a6440'
    ctx.fill()
  } else {
    const dx = facing === 'left' ? -r * 0.15 : facing === 'right' ? r * 0.15 : 0
    const spread = facing === 'down' ? r * 0.18 : r * 0.11
    ctx.fillStyle = '#2b211a'
    for (const side of facing === 'down' ? [-1, 1] : [1]) {
      ctx.beginPath()
      ctx.ellipse(cx + dx + side * spread, cy + r * 0.06, r * 0.075, r * 0.095, 0, 0, TAU)
      ctx.fill()
    }
    if (facing === 'down') {
      ctx.beginPath()
      ctx.arc(cx, cy + r * 0.14, r * 0.16, 0.35, Math.PI - 0.35)
      ctx.lineWidth = lw * 0.7
      ctx.strokeStyle = '#a3764d'
      ctx.stroke()
    }
  }

  drawHat(ctx, seat, cx, cy - r * 0.38, r, lw)
}

/** The hats, drawn from the same anchor so they can be compared side by side. */
function drawHat(
  ctx: CanvasRenderingContext2D,
  seat: SeatIdentity,
  cx: number,
  cy: number,
  r: number,
  lw: number,
): void {
  ctx.lineWidth = lw
  ctx.strokeStyle = '#b3aa99'
  ctx.fillStyle = '#fdfaf3'

  switch (seat.hat) {
    case 'toque': {
      // Tall and puffy: the silhouette that says "chef" from across a room.
      ctx.beginPath()
      ctx.arc(cx - r * 0.3, cy - r * 0.3, r * 0.27, 0, TAU)
      ctx.arc(cx + r * 0.3, cy - r * 0.3, r * 0.27, 0, TAU)
      ctx.arc(cx, cy - r * 0.56, r * 0.33, 0, TAU)
      ctx.fill()
      ctx.beginPath()
      roundRect(ctx, cx - r * 0.52, cy - r * 0.22, r * 1.04, r * 0.34, r * 0.1)
      ctx.fill()
      ctx.stroke()
      break
    }
    case 'cap': {
      // Flat, with a brim out front. Nothing like a toque in outline, which is
      // the entire point of having two.
      ctx.beginPath()
      ctx.arc(cx, cy + r * 0.1, r * 0.46, Math.PI, TAU)
      ctx.closePath()
      ctx.fill()
      ctx.stroke()
      ctx.beginPath()
      roundRect(ctx, cx - r * 0.6, cy + r * 0.06, r * 1.2, r * 0.16, r * 0.08)
      ctx.fill()
      ctx.stroke()
      break
    }
    case 'bandana': {
      ctx.beginPath()
      ctx.moveTo(cx - r * 0.5, cy + r * 0.14)
      ctx.lineTo(cx + r * 0.5, cy + r * 0.14)
      ctx.lineTo(cx, cy - r * 0.42)
      ctx.closePath()
      ctx.fill()
      ctx.stroke()
      break
    }
    default: {
      ctx.beginPath()
      ctx.arc(cx, cy + r * 0.16, r * 0.42, Math.PI, TAU)
      ctx.closePath()
      ctx.fill()
      ctx.stroke()
      ctx.beginPath()
      ctx.arc(cx, cy - r * 0.3, r * 0.12, 0, TAU)
      ctx.fill()
      ctx.stroke()
      break
    }
  }
}

/**
 * The same chef, at badge size, facing the reader.
 *
 * The HUD roster uses it so the two silhouettes are named where a player can
 * study them, rather than only appearing on a 60-pixel tile in the middle of a
 * service. Teaching the vocabulary is what makes "the toque has the pan" a
 * sentence two strangers on a voice call can actually use.
 */
export function drawChefBadge(
  ctx: CanvasRenderingContext2D,
  seat: SeatIdentity,
  size: number,
): void {
  const r = size * 0.36
  ctx.save()
  ctx.translate(0, size * 0.06)
  drawChef(ctx, seat, size / 2, size / 2, r, 'down')
  ctx.restore()
}
